/**
 * Der Spielbildschirm.
 *
 * Alles, was der Besucher waehrend eines Vortrags braucht, ohne zu
 * scrollen: Raumleiste, Karte, Legende. Alles andere kommt als Blatt von
 * unten, damit die Karte stehen bleibt.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { AskSheet } from '../components/AskSheet'
import { Card, Legend } from '../components/Card'
import { LiveNote, RoomBar, Toasts } from '../components/Chrome'
import { Footer } from '../components/Footer'
import { TermSheet } from '../components/TermSheet'
import { useLang } from '../lang'
import { ensureSession } from '../lib/api'
import type { Tone } from '../lib/i18n'
import { useGame, watchConnectivity } from '../lib/store'

const INTRO_KEY = 'bsb.intro'
const AUTO_KEY = 'bsb.autoinfo'

export function Room() {
  const { roomId = '' } = useParams()
  const { t } = useLang()
  const navigate = useNavigate()
  const game = useGame()
  const [inspect, setInspect] = useState<string | null>(null)
  const [intro, setIntro] = useState(false)
  const [ready, setReady] = useState(false)
  const [mischt, setMischt] = useState(false)
  const [tone] = useState<Tone>('mundart')
  // Nach dem Markieren das Blatt zeigen. Wer eine Floskel hoert, will sie
  // markieren **und** wissen, was sie heisst. Abschaltbar im Blatt selbst.
  const [autoZeigen, setAutoZeigen] = useState(() => {
    try {
      return localStorage.getItem(AUTO_KEY) !== '0'
    } catch {
      return true
    }
  })

  useEffect(() => {
    let alive = true
    void (async () => {
      try {
        await ensureSession()
        if (!alive) return
        await game.enter(roomId)
        if (alive) setReady(true)
      } catch (e) {
        if (alive) game.setError((e as { code?: string }).code ?? 'generic')
      }
    })()
    const stop = watchConnectivity()
    return () => {
      alive = false
      stop()
      game.leave()
    }
    // Nur beim Raumwechsel neu aufbauen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId])

  useEffect(() => {
    try {
      if (!localStorage.getItem(INTRO_KEY)) setIntro(true)
    } catch {
      /* egal */
    }
  }, [])

  const closeIntro = useCallback(() => {
    setIntro(false)
    try {
      localStorage.setItem(INTRO_KEY, '1')
    } catch {
      /* egal */
    }
  }, [])

  const istUebung = game.room?.practice === true

  /** Ein Tipp markiert und zeigt, was dahintersteht. */
  const tippen = useCallback(
    async (cell: number) => {
      const termId = game.cells[cell]
      const warMarkiert = Boolean(game.status[cell])
      if (warMarkiert) {
        // Ein zweiter Tipp nimmt nicht mehr zurueck, sondern schlaegt nach.
        // Zuruecknehmen steht im Blatt, wo es niemanden ueberrascht.
        if (termId) setInspect(termId)
        return
      }
      await game.toggle(cell)
      if (autoZeigen && termId) setInspect(termId)
    },
    [autoZeigen, game],
  )

  const neueKarte = useCallback(async () => {
    setMischt(true)
    try {
      await game.reshuffle()
    } finally {
      setMischt(false)
    }
  }, [game])

  const winning = useMemo(() => {
    const set = new Set<number>()
    for (const b of game.bingos) for (const c of b.line) set.add(c)
    return set
  }, [game.bingos])

  const question = game.questions[0]
  const hasBingo = game.bingos.length > 0
  const oneAway = game.oneAway.length > 0
  const provisional = game.lines.some((l) => l.provisional)

  if (!ready && !game.error) {
    return <div className="page"><p className="muted">{t('common.loading')}</p></div>
  }
  if (game.error && !game.room) {
    return (
      <div className="page stack">
        <h2>{t(`err.${game.error}` as 'err.generic')}</h2>
        <Link className="btn" to="/">{t('common.back')}</Link>
      </div>
    )
  }

  const running = game.room?.status === 'running'

  return (
    <div className="page">
      {game.room ? <RoomBar room={game.room} pulse={game.pulse} marks2min={game.marks2min} /> : null}

      {!running && game.room ? (
        <div className="banner banner--quiet" style={{ marginBottom: 10 }}>
          {game.room.status === 'ended' ? t('room.ended')
            : game.room.status === 'paused' ? t('room.paused')
            : t('room.waiting')}
        </div>
      ) : null}

      {hasBingo ? (
        <div className="banner" style={{ marginBottom: 10 }}>
          <div className="row row--between">
            <div>
              <strong>{t('room.bingo')}</strong>{' '}
              {istUebung ? null : (
                <>
                  <span className="pts">{game.bingos[0]?.grade}</span>{' '}
                  <span className="muted small">· {game.bingos[0]?.sources}×</span>
                </>
              )}
            </div>
            {istUebung ? null : (
              <Link className="btn" to={`/room/${roomId}/melden`}>{t('room.report')}</Link>
            )}
          </div>
          {istUebung ? (
            <p className="small" style={{ margin: '6px 0 0' }}>{t('room.practiceBingo')}</p>
          ) : null}
        </div>
      ) : provisional ? (
        <div className="banner banner--quiet" style={{ marginBottom: 10 }}>{t('room.provisional')}</div>
      ) : oneAway ? (
        <div className="banner banner--quiet" style={{ marginBottom: 10 }}>{t('room.oneAway')}</div>
      ) : null}

      <div className="tools">
        {istUebung ? (
          <button type="button" className="btn btn--ghost" disabled={mischt}
            onClick={() => void neueKarte()}>
            {mischt ? t('common.loading') : t('room.newCard')}
          </button>
        ) : null}
        <span className="kicker tools__hint">{t('card.tapHint')}</span>
      </div>

      <Card
        cells={game.cells}
        terms={game.terms}
        status={game.status}
        winning={winning}
        flash={game.flash}
        disabled={!running}
        onToggle={(cell) => void tippen(cell)}
        onInspect={setInspect}
      />

      {istUebung ? null : (
        <WarumZaehltNichts
          status={game.status}
          players={game.room?.players ?? 0}
          rules={game.room?.rules ?? {}}
        />
      )}

      <div style={{ marginTop: 12 }}>
        <Legend />
      </div>

      <div className="actions">
        {hasBingo && !istUebung ? (
          <Link className="btn" to={`/room/${roomId}/melden`}>{t('room.report')}</Link>
        ) : null}
        <Link className="btn btn--ghost" to={`/room/${roomId}/rangliste`}>
          {t('room.board')}
        </Link>
        {game.room?.myRoles?.includes('game_master') ? (
          <Link className="btn btn--ghost" to={`/room/${roomId}/spielleiter`}>
            {t('room.gm')}
          </Link>
        ) : null}
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => {
            game.leave()
            navigate('/')
          }}
        >
          {t('room.leave')}
        </button>
      </div>

      <LiveNote state={game.live} queued={game.queued} />
      <Toasts toasts={game.toasts} onDismiss={game.dismissToast} />

      {inspect && game.terms[inspect] ? (
        <TermSheet
          term={game.terms[inspect]!}
          marked={game.cells.some((id, i) => id === inspect && game.status[i])}
          autoOpen={autoZeigen}
          onAutoOpenChange={(an) => {
            setAutoZeigen(an)
            try {
              localStorage.setItem(AUTO_KEY, an ? '1' : '0')
            } catch {
              /* egal */
            }
          }}
          onUnmark={() => {
            const cell = game.cells.findIndex((id, i) => id === inspect && game.status[i])
            if (cell >= 0) void game.toggle(cell)
            setInspect(null)
          }}
          onClose={() => setInspect(null)}
        />
      ) : null}

      {question ? (
        <AskSheet
          question={question}
          term={game.terms[question.termId]}
          others={Math.max(0, (game.room?.players ?? 1) - 1)}
          tone={tone}
          onAnswer={(markId, keep) => void game.answer(markId, keep)}
        />
      ) : null}

      {intro ? <Intro onClose={closeIntro} /> : null}
      <Footer />
    </div>
  )
}

/**
 * Warum ein Feld nicht zaehlt.
 *
 * Ohne diesen Kasten sieht ein Spieler schraffierte Kaestchen und haelt das
 * Spiel fuer kaputt. Es ist aber nicht kaputt, sondern es wartet: ein Feld
 * zaehlt erst, wenn jemand anders dieselbe Floskel gehoert hat. Das ist der
 * ganze Punkt des Spiels, und genau der stand bisher nirgends auf dem
 * Bildschirm.
 */
function WarumZaehltNichts({
  status,
  players,
  rules,
}: {
  status: Record<number, { grade: string }>
  players: number
  rules: Record<string, unknown>
}) {
  const { t } = useLang()
  const felder = Object.values(status)
  if (felder.length === 0) return null

  const gueltig = felder.filter((c) => ['A1', 'B2', 'C3'].includes(c.grade)).length
  const offen = felder.filter((c) => c.grade === 'F6').length
  if (gueltig > 0 || offen === 0) return null

  const minCrowd = Number(rules.min_crowd ?? 3)
  const allein = players < minCrowd

  return (
    <div className="banner" style={{ marginTop: 12 }}>
      <h3>{t('why.title')}</h3>
      <p>{allein ? t('why.smallRoom') : t('why.needSource', { n: offen })}</p>
    </div>
  )
}

/** Drei Schritte, einmal (FR-ON-04). */
function Intro({ onClose }: { onClose: () => void }) {
  const { t } = useLang()
  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={t('intro.title')}>
      <div className="sheet__inner stack">
        <h2>{t('intro.title')}</h2>
        <ol style={{ paddingLeft: 20, margin: 0, fontSize: 17 }}>
          <li style={{ marginBottom: 8 }}>{t('intro.step1')}</li>
          <li style={{ marginBottom: 8 }}>{t('intro.step2')}</li>
          <li style={{ marginBottom: 8 }}>{t('intro.step3')}</li>
          <li>{t('intro.step4')}</li>
        </ol>
        <button type="button" className="btn btn--block" onClick={onClose}>
          {t('intro.start')}
        </button>
      </div>
    </div>
  )
}
